import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import CircularProgress from "@mui/material/CircularProgress";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import LockIcon from "@mui/icons-material/Lock";
import EmailIcon from "@mui/icons-material/Email";
import LoginIcon from "@mui/icons-material/Login";
import { login as apiLogin } from "../../services/auth";
import { useAuth } from "../../Context/AuthContext";
import { googleAuth } from "../../services/auth";

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [form, setForm] = useState({
    email: "",
    password: "",
    fcm_token: "web",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      localStorage.removeItem("token");
      sessionStorage.removeItem("token");

      const payload = {
        email: form.email.trim(),
        password: form.password,
        fcm_token: form.fcm_token || "web",
      };

      const res = await apiLogin(payload, { mode: "form" });

      if (res?.data?.user) localStorage.setItem("user", JSON.stringify(res.data.user));
      if (res?.data?.token) localStorage.setItem("token", res.data.token);

      login(res.data.user, res.data.token);

      const role = res?.data?.user?.type || res?.data?.user?.role;

      switch (role) {
        case "admin":
          navigate("/admin/dashboard", { replace: true });
          break;
        case "client":
          navigate("/myorders", { replace: true });
          break;
        case "user":
          navigate("/", { replace: true });
          break;
        default:
          navigate("/", { replace: true });
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Login failed, please try again");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      setError("");
      const res = await googleAuth();
      const url = res?.url;
      if (!url) throw new Error("Google auth url not returned");
      window.location.assign(url);
    } catch (err) {
      console.log("GOOGLE AUTH ERR:", err?.response?.status, err?.response?.data);
      setError(err?.response?.data?.message || err?.message || "Google login failed");
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default",
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: "-50%",
          right: "-20%",
          width: "80%",
          height: "80%",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(213, 170, 47, 0.08) 0%, transparent 70%)",
          animation: "float 20s ease-in-out infinite",
        },
        "&::after": {
          content: '""',
          position: "absolute",
          bottom: "-50%",
          left: "-20%",
          width: "80%",
          height: "80%",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(213, 170, 47, 0.08) 0%, transparent 70%)",
          animation: "float 15s ease-in-out infinite reverse",
        },
        "@keyframes float": {
          "0%, 100%": { transform: "translate(0, 0)" },
          "50%": { transform: "translate(50px, 50px)" },
        },
      }}
    >
      <Container maxWidth="sm" sx={{ position: "relative", zIndex: 1 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 4,
            border: "1px solid",
            borderColor: "grey.100",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
            backdropFilter: "blur(10px)",
            animation: "fadeInUp 0.6s ease-out",
            "@keyframes fadeInUp": {
              from: { opacity: 0, transform: "translateY(30px)" },
              to: { opacity: 1, transform: "translateY(0)" },
            },
          }}
        >
          {/* Header */}
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                mx: "auto",
                mb: 2,
                borderRadius: 3,
                background: "linear-gradient(135deg, #d5aa2f 0%, #e6c76a 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 8px 24px rgba(213, 170, 47, 0.3)",
              }}
            >
              <LockIcon sx={{ fontSize: 40, color: "white" }} />
            </Box>
            <Typography
              variant="h4"
              fontWeight={800}
              sx={{
                mb: 1,
                background: "linear-gradient(135deg, #d5aa2f 0%, #b08c26 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              تسجيل الدخول
            </Typography>
            <Typography variant="body2" color="text.secondary" fontWeight={600}>
              مرحباً بك في متجر DIDA MAX 
            </Typography>
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 3,
                borderRadius: 3,
                animation: "shake 0.5s ease-in-out",
                "@keyframes shake": {
                  "0%, 100%": { transform: "translateX(0)" },
                  "25%": { transform: "translateX(-10px)" },
                  "75%": { transform: "translateX(10px)" },
                },
              }}
            >
              {error}
            </Alert>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit}>
            <Stack spacing={3}>
              <TextField
                type="email"
                name="email"
                label="البريد الإلكتروني"
                placeholder="your@email.com"
                value={form.email}
                onChange={handleChange}
                required
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon sx={{ color: "primary.main" }} />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                type={showPassword ? "text" : "password"}
                name="password"
                label="كلمة المرور"
                placeholder="••••••••"
                value={form.password}
                onChange={handleChange}
                required
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon sx={{ color: "primary.main" }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <LoginIcon />}
                sx={{
                  py: 1.5,
                  fontSize: "1rem",
                  fontWeight: 800,
                  borderRadius: 3,
                  textTransform: "none",
                  background: "linear-gradient(135deg, #d5aa2f 0%, #e6c76a 100%)",
                  boxShadow: "0 4px 16px rgba(213, 170, 47, 0.35)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #b08c26 0%, #d5aa2f 100%)",
                    boxShadow: "0 6px 24px rgba(213, 170, 47, 0.45)",
                  },
                  "&:disabled": {
                    background: "linear-gradient(135deg, #e0e0e0 0%, #bdbdbd 100%)",
                  },
                }}
              >
                {loading ? "جاري تسجيل الدخول..." : "تسجيل الدخول"}
              </Button>
            </Stack>
          </form>

          {/* Divider */}
          <Divider sx={{ my: 3 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              أو
            </Typography>
          </Divider>

          {/* Social & Actions */}
          <Stack spacing={2}>
            <Button
              variant="outlined"
              size="large"
              onClick={handleGoogleLogin}
              startIcon={
                <img
                  src="https://developers.google.com/identity/images/g-logo.png"
                  alt="Google"
                  style={{ width: "20px", height: "20px" }}
                />
              }
              sx={{
                py: 1.5,
                borderRadius: 3,
                textTransform: "none",
                fontWeight: 700,
                borderWidth: 2,
                "&:hover": {
                  borderWidth: 2,
                  bgcolor: "grey.50",
                },
              }}
            >
              تسجيل الدخول باستخدام Google
            </Button>

            <Button
              variant="text"
              onClick={() => navigate("/reset-password", { state: { email: form.email } })}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                color: "primary.main",
                "&:hover": {
                  bgcolor: "primary.50",
                },
              }}
            >
              نسيت كلمة المرور؟
            </Button>
          </Stack>

          {/* Sign Up Link */}
          <Box
            sx={{
              mt: 3,
              pt: 3,
              borderTop: "1px solid",
              borderColor: "grey.100",
              textAlign: "center",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              ليس لديك حساب؟{" "}
              <Typography
                component="span"
                onClick={() => navigate("/register")}
                sx={{
                  color: "primary.main",
                  fontWeight: 700,
                  cursor: "pointer",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                إنشاء حساب جديد
              </Typography>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Login;
